import React from 'react';
import { get } from 'lodash';
import { Col } from 'react-bootstrap';
import RenderingTile from 'components/molecules/RenderingTile';
import CallOutValueWithChange from 'components/molecules/CallOutValueWithChange';

/**
 * Validation function and passed arguments
 *
 * @typedef {object} ValidationBlock
 * @property {function} fxn      If present the value for the associated key must pass the validation or the Callout
 *                               will not be shown.
 * @property {object} passedArgs Any arguments to be spread on the execution of the validation
 */

/**
 * Settings Block used to generate a callout
 *
 * @typedef CalloutSetting {object}
 * @property {string} key           Key used to access data in `callouts` data block
 * @property {string} title         Display title for callout
 * @property {string} format        Number formatting to used on the value.
 * @property {string} [helpKey]     Used to pass a specific key for generating Help Text
 * @property {boolean} [invert]     Invert the Change Delta arrow.
 *
 * @property {ValidationBlock} [validation] If present the value for the associated key must pass the validation or the
 *                                          Callout will not be shown.
 *
 * @property {component} [component] A React Component to use in place of the default `CallOutValueWithChange`
 * @property {object} [props]        Properties to be passed directly to the `component` that is passed in.
 */

/**
 * Callout Data Block - Data object with all Callout data to be referenced
 *
 * @typedef {object} CalloutData
 * @property {boolean} completeData Notes if there is a complete data range for the Callout
 * @property {object[]} sparkline   Row level data that is used to generate the sparkline charts
 * @property {...number} keys       Any number of keys with numerical values
 */

/**
 * Generator used to generate a Row of Callouts with appropriate Column settings and predictable export keys
 *
 * @param {object} params
 * @param {string} params.stub                      Base name of the exportName used in the PDF and CSV export process
 * @param {boolean} [params.hasCalloutData]         Basic control flag for determining rendering state.
 * @param {number|boolean} params.calloutWindow     Number of days in the current window
 * @param {CalloutSetting[]} params.calloutSettings Array of settings objects that are 1:1 with the Callouts to be
 *                                                  generated
 * @param {CalloutData} params.callouts             Data object with all Callout data to be referenced
 *
 * @returns {component} (
 *     <Row>
 *         <Col {...}>
 *             <CallOutValueWithChange {...}>
 *         </Col>
 *         <Col {...}>
 *             <CallOutValueWithChange {...}>
 *         </Col>
 *     </Row>
 * )
 */
exports.generateCalloutComponents = ({
    stub, calloutSettings, hasCalloutData, callouts, calloutWindow, isLabelCoded, mdSizeOverride
}) => {
    // Filter calloutSettings by the validation result
    const filteredCalloutSettings = calloutSettings.filter((setting) => {
        // If `validation` is in place, execute it.
        if ('validation' in setting) {
            const { fxn, passedArgs = {} } = setting.validation;
            return fxn({ key: `current_${setting.key}`, data: callouts, ...passedArgs });
        }
        return true;
    });

    const mdSize = mdSizeOverride || Math.max(12 / filteredCalloutSettings.length, 3);

    return filteredCalloutSettings.map((config, index) => {
        if ('component' in config) {
            const hasData = get(config, 'hasData', hasCalloutData);
            return (
                <Col xs={12} md={mdSize} key={index}>
                    {hasData ? (
                        <config.component
                            {...config.props}
                        />
                    ) : (
                        <RenderingTile height='180px' />
                    )}
                </Col>
            );
        }

        return (
            <Col xs={12} md={mdSize} key={index}>
                {hasCalloutData ? (
                    <CallOutValueWithChange
                        exportName={`${stub}-callout-${index + 1}`}
                        title={config.title}
                        type={config.key}
                        helpKey={config.helpKey || config.key}
                        format={config.format}
                        invert={!!config.invert}
                        isLabelCoded={isLabelCoded}
                        data={{
                            value: callouts[`current_${config.key}`],
                            change: Math.round(
                                callouts[`${config.key}_per_delta`], 0),
                            completeData: callouts.completeData,
                            sparkline: callouts.sparkline,
                            calloutWindow
                        }}
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='180px' />
                )}
            </Col>
        );
    });
};



// WEBPACK FOOTER //
// ./src/helpers/componentGenerators.js